import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Pagination from './Pagination';
import TagInput from './TagInput';
import { useConfirm } from '../context/ConfirmContext';
import {
  addPostImages, createPost, deletePost, getTags, listAdminPosts, removePostImage, updatePost,
} from '../api/galleryApi';

// ── Shared styles (mirrors BrandstoreManagement's local style consts) ──────
const card  = { background: '#fff', border: '1.5px solid #E5E7EB', borderRadius: 12, padding: '20px 24px', marginBottom: 16 };
const label = { display: 'block', fontSize: 12, fontWeight: 600, color: '#6B7280', marginBottom: 5 };
const input = { width: '100%', padding: '9px 11px', border: '1.5px solid #E5E7EB', borderRadius: 7, fontSize: 14, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box', color: '#111827' };
const btnPrimary = { padding: '9px 20px', background: 'var(--color-primary,#F29213)', color: '#fff', border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 700, cursor: 'pointer' };
const btnGhost  = { padding: '8px 16px', background: '#fff', color: '#4B5563', border: '1.5px solid #E5E7EB', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const btnDanger = { padding: '6px 12px', background: '#fff', color: '#B91C1C', border: '1.5px solid #FCA5A5', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer' };
const tagPill = { fontSize: 10, fontWeight: 700, color: '#B15400', background: '#FEF3E2', borderRadius: 999, padding: '2px 8px' };

// ── New/Edit Post modal ─────────────────────────────────────────────────────
function PostModal({ post, tagSuggestions, onClose, onSaved }) {
  const confirm = useConfirm();
  const [caption, setCaption] = useState(post?.caption || '');
  const [tags, setTags] = useState(post?.tags || []);
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // A native file dialog only multi-selects within one folder per invocation — so each
  // pick is appended rather than replacing the previous one, letting the picker be
  // reopened to grab photos from a different folder without losing what's already staged.
  const filePreviews = useMemo(() => files.map((f) => URL.createObjectURL(f)), [files]);
  useEffect(() => () => filePreviews.forEach((url) => URL.revokeObjectURL(url)), [filePreviews]);

  const handleFilesSelected = (e) => {
    const picked = Array.from(e.target.files || []);
    setFiles((prev) => [...prev, ...picked]);
    e.target.value = ''; // allow picking again (even the same file) without the input ignoring it
  };

  const removeStagedFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!post && files.length === 0) {
      setError('At least one photo is required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (post) {
        await updatePost(post.id, { caption, tags });
        if (files.length > 0) await addPostImages(post.id, files);
      } else {
        await createPost(caption, tags, files);
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save post.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveImage = async (imageId) => {
    const ok = await confirm({
      title: 'Remove this photo?',
      message: 'It will be taken off this post.',
      confirmLabel: 'Remove',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await removePostImage(post.id, imageId);
      onSaved();
    } catch {
      setError('Failed to remove photo.');
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16, boxSizing: 'border-box' }}>
      <div style={{ ...card, width: 'min(480px, 100%)', maxHeight: '85vh', overflowY: 'auto', marginBottom: 0 }}>
        <h3 style={{ margin: '0 0 16px', fontSize: 17, fontWeight: 700 }}>{post ? 'Edit Post' : 'New Post'}</h3>

        {error && <div style={{ color: '#B91C1C', fontSize: 13, marginBottom: 12 }}>{error}</div>}

        {post && post.images?.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <label style={label}>Current photos</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {post.images.map(img => (
                <div key={img.id} style={{ position: 'relative' }}>
                  <img src={img.url} alt="" style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 6 }} />
                  <button
                    onClick={() => handleRemoveImage(img.id)}
                    title="Remove photo"
                    style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%', border: 'none', background: '#B91C1C', color: '#fff', fontSize: 12, cursor: 'pointer', lineHeight: '20px', padding: 0 }}
                  >×</button>
                </div>
              ))}
            </div>
          </div>
        )}

        <label style={label}>{post ? 'Add more photos' : 'Photos'}</label>
        <input
          type="file" accept="image/*" multiple
          onChange={handleFilesSelected}
          style={{ ...input, marginBottom: files.length > 0 ? 8 : 12 }}
        />
        {files.length > 0 && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
            {files.map((file, i) => (
              <div key={i} style={{ position: 'relative' }}>
                <img src={filePreviews[i]} alt="" style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 6 }} />
                <button
                  onClick={() => removeStagedFile(i)}
                  title="Remove"
                  style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%', border: 'none', background: '#B91C1C', color: '#fff', fontSize: 12, cursor: 'pointer', lineHeight: '20px', padding: 0 }}
                >×</button>
              </div>
            ))}
          </div>
        )}

        <label style={label}>Caption</label>
        <textarea
          value={caption} onChange={e => setCaption(e.target.value)}
          style={{ ...input, minHeight: 70, marginBottom: 12, resize: 'vertical' }}
          placeholder="What's happening in these photos?"
        />

        <label style={label}>Tags</label>
        <div style={{ marginBottom: 20 }}>
          <TagInput value={tags} onChange={setTags} suggestions={tagSuggestions} placeholder="e.g. diwali — press Enter" />
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button style={btnGhost} onClick={onClose} disabled={saving}>Cancel</button>
          <button style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────────────
export default function GalleryManagement() {
  const confirm = useConfirm();
  const [posts, setPosts] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingPost, setEditingPost] = useState(null);
  const [showNewPost, setShowNewPost] = useState(false);
  const [tagSuggestions, setTagSuggestions] = useState([]);

  const loadTags = useCallback(() => {
    getTags().then(r => setTagSuggestions(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => { loadTags(); }, [loadTags]);

  const loadPosts = useCallback(() => {
    setLoading(true);
    listAdminPosts(page, 20, search)
      .then(r => { setPosts(r.data.content || []); setTotalPages(r.data.totalPages || 0); })
      .catch(() => { setPosts([]); setTotalPages(0); })
      .finally(() => setLoading(false));
  }, [page, search]);

  useEffect(() => { loadPosts(); }, [loadPosts]);

  const handleDelete = async (post) => {
    const ok = await confirm({
      title: 'Delete this post?',
      message: 'The post and all its photos will be removed from the public gallery. This cannot be undone.',
      confirmLabel: 'Delete post',
      tone: 'danger',
    });
    if (!ok) return;
    await deletePost(post.id);
    loadPosts();
  };

  const handleSaved = () => {
    setShowNewPost(false);
    setEditingPost(null);
    loadPosts();
    loadTags();
  };

  const handleTagClick = (tag) => {
    setSearch(tag);
    setPage(0);
  };

  return (
    <div style={{ padding: '24px 0' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: '#111827', margin: '0 0 4px', letterSpacing: '-0.5px' }}>Gallery</h1>
        <p style={{ fontSize: 13, color: '#9CA3AF', margin: 0 }}>
          Upload showcase photos and tag them — the public gallery at /gallery needs no login to view.
        </p>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 16 }}>
        <input
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(0); }}
          placeholder="Search by tag…"
          style={{ ...input, width: 'min(260px, 100%)' }}
        />
        <button style={btnPrimary} onClick={() => setShowNewPost(true)}>+ New Post</button>
      </div>

      {loading ? (
        <p style={{ color: '#9CA3AF' }}>Loading…</p>
      ) : posts.length === 0 ? (
        <p style={{ color: '#9CA3AF' }}>No posts found.</p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14 }}>
          {posts.map(post => (
            <div key={post.id} style={{ ...card, padding: 0, overflow: 'hidden', marginBottom: 0 }}>
              {post.images?.[0] && (
                <img src={post.images[0].url} alt="" style={{ width: '100%', height: 140, objectFit: 'cover', display: 'block' }} />
              )}
              <div style={{ padding: 12 }}>
                {post.tags?.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
                    {post.tags.map(tag => (
                      <button key={tag} onClick={() => handleTagClick(tag)} style={{ ...tagPill, border: 'none', cursor: 'pointer' }}>{tag}</button>
                    ))}
                  </div>
                )}
                <div style={{ fontSize: 13, color: '#374151', marginBottom: 10, minHeight: 34, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                  {post.caption || <em style={{ color: '#9CA3AF' }}>No caption</em>}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button style={btnGhost} onClick={() => setEditingPost(post)}>Edit</button>
                  <button style={btnDanger} onClick={() => handleDelete(post)}>Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {totalPages > 1 && <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />}

      {showNewPost && <PostModal tagSuggestions={tagSuggestions} onClose={() => setShowNewPost(false)} onSaved={handleSaved} />}
      {editingPost && <PostModal post={editingPost} tagSuggestions={tagSuggestions} onClose={() => setEditingPost(null)} onSaved={handleSaved} />}
    </div>
  );
}
